import type { InfiniteData, QueryClient } from '@tanstack/react-query'
import type { api, CleaningJob } from '@/api/client'

type JobsPage = Awaited<ReturnType<typeof api.getCleaningJobs>>
type JobDetail = Awaited<ReturnType<typeof api.getCleaningJob>>

/**
 * Put the job an action just returned into every cached list and drawer that
 * already shows it.
 *
 * **This is not an optimistic update.** Nothing here is guessed: every action
 * endpoint answers with the whole job as admin-api now holds it, and that is
 * the record written in. Invalidating alone meant the screen kept rendering
 * the pre-action job for as long as the refetch took - a second or two on a
 * board of 45 cleanings, during which a row the operator had just acked still
 * read "No ack" underneath a toast saying it was recorded, which is the one
 * thing a confirmation must not do (CAP-525).
 *
 * The caller still invalidates afterwards. This closes the visible gap; the
 * refetch is what keeps ordering, grouping and the facet counts honest, since
 * an action can move a job between days or out of a filtered set entirely and
 * only the server decides where it then belongs.
 */
export function applyCleaningJob(queryClient: QueryClient, job: CleaningJob) {
  // Every filter combination the operator has visited is its own cache entry,
  // so this matches on the key PREFIX rather than rebuilding the current one:
  // the drawer does not know the filters, and a stale sibling entry is exactly
  // what the next tab switch would render.
  //
  // That prefix covers TWO shapes, which is the whole reason this is written as
  // a shape test rather than a cast. The board's upcoming list is an infinite
  // query, so its entry is `{ pages: [...] }`; the job calendar's month
  // (`['cleaning', 'jobs', 'calendar', ...]`) is a plain `useQuery` holding one
  // page. Assuming the first shape threw `Cannot read properties of undefined
  // (reading 'map')` inside the mutation's success handler for anyone who had
  // opened a month in the last few minutes - the calendar's entry outlives its
  // dialog by `gcTime`, so closing it does not help.
  queryClient.setQueriesData<CachedJobs>({ queryKey: ['cleaning', 'jobs'] }, current => {
    if (!current) return current
    if (isInfinite(current)) {
      let touched = false
      const pages = current.pages.map(page => {
        const patched = patchPage(page, job)
        if (patched === page) return page
        touched = true
        return patched
      })
      // Returning `current` unchanged when nothing matched keeps the identity
      // stable, so a list that does not hold this job does not re-render.
      return touched ? { ...current, pages } : current
    }
    if (isPage(current)) return patchPage(current, job)
    // Some third shape added under this prefix later: left alone rather than
    // guessed at, so a new cache entry can never crash an action.
    return current
  })

  queryClient.setQueryData<JobDetail>(['cleaning', 'job', job.property_id, job.id], current =>
    // Only patched, never created: the detail response carries the booking
    // behind the job as well, which an action's response does not, so a cache
    // entry written from here would be a detail record missing its source.
    current ? { ...current, job } : current
  )
}

/** Either shape cached under `['cleaning', 'jobs', ...]`. */
type CachedJobs = InfiniteData<JobsPage, number> | JobsPage

function isInfinite(data: CachedJobs): data is InfiniteData<JobsPage, number> {
  return Array.isArray((data as InfiniteData<JobsPage, number>).pages)
}

function isPage(data: CachedJobs): data is JobsPage {
  return Array.isArray((data as JobsPage).jobs)
}

/** The same page, or a copy with this job replaced. */
function patchPage(page: JobsPage, job: CleaningJob): JobsPage {
  if (!page.jobs.some(existing => isSameJob(existing, job))) return page
  return { ...page, jobs: page.jobs.map(existing => (isSameJob(existing, job) ? job : existing)) }
}

function isSameJob(a: CleaningJob, b: CleaningJob) {
  return a.property_id === b.property_id && a.id === b.id
}
